import { useEffect, useMemo, useState } from 'react';
import {
  IconClose,
  IconHome,
  IconKey,
  IconPalette,
  IconShield,
  IconUser,
} from '../../components/icons';
import SpApiSettings from '../auth/SpApiSettings';
import AccountSettings from './AccountSettings';
import AssignUserSettings from './AssignUserSettings';
import ThemeSettings from './ThemeSettings';

const SECTIONS = [
  { id: 'general', label: 'General', icon: IconHome },
  { id: 'appearance', label: 'Appearance', icon: IconPalette, roles: ['admin', 'super_admin', 'workspace_user'] },
  { id: 'sp-api', label: 'SP-API', icon: IconKey, roles: ['admin'] },
  { id: 'assign-user', label: 'Assign User', icon: IconShield, roles: ['admin'] },
  { id: 'account', label: 'Account', icon: IconUser },
];

function SettingsModal({ open, onClose, user, token, onUserUpdate }) {
  const [activeSection, setActiveSection] = useState('general');

  const sections = useMemo(() => {
    return SECTIONS.filter((section) => {
      if (!section.roles) {
        return true;
      }

      return Boolean(token) && section.roles.includes(user?.role);
    });
  }, [user?.role, token]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!sections.some((section) => section.id === activeSection)) {
      setActiveSection(sections[0]?.id || 'account');
    }
  }, [sections, activeSection]);

  if (!open) {
    return null;
  }

  const activeMeta = sections.find((section) => section.id === activeSection) || sections[0];

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center bg-black/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="crm-panel grid h-[min(740px,calc(100vh-2rem))] w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl md:grid-cols-[15rem_1fr] border border-[rgb(var(--crm-border))]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <aside className="grid grid-rows-[auto_1fr] border-r border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))]">
          <div className="flex items-center justify-between border-b border-[rgb(var(--crm-border))] px-4 py-4">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[rgb(var(--crm-accent))]" />
              <h2 id="settings-modal-title" className="text-sm font-bold tracking-tight text-[rgb(var(--crm-text))]">
                Settings
              </h2>
            </div>
            <button
              type="button"
              className="inline-grid h-7 w-7 place-items-center rounded-lg text-[rgb(var(--crm-text-muted))] transition hover:bg-red-500/10 hover:text-red-600"
              onClick={onClose}
              aria-label="Close settings"
            >
              <IconClose className="h-4 w-4" />
            </button>
          </div>

          <nav className="grid content-start gap-1 p-3" aria-label="Settings sections">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;

              return (
                <button
                  key={section.id}
                  type="button"
                  className={[
                    'group flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-all duration-150',
                    isActive
                      ? 'bg-[rgb(var(--crm-surface))] text-[rgb(var(--crm-text))] font-bold shadow-2xs border border-[rgb(var(--crm-border))]'
                      : 'text-[rgb(var(--crm-text-muted))] hover:bg-[rgb(var(--crm-surface)/0.7)] hover:text-[rgb(var(--crm-text))]',
                  ].join(' ')}
                  onClick={() => setActiveSection(section.id)}
                >
                  <Icon
                    className={`h-4 w-4 shrink-0 transition-colors ${
                      isActive
                        ? 'text-[rgb(var(--crm-accent))]'
                        : 'text-[rgb(var(--crm-text-muted))] group-hover:text-[rgb(var(--crm-text))]'
                    }`}
                  />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="grid min-h-0 grid-rows-[auto_1fr]">
          <div className="border-b border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] px-6 py-4.5">
            <h3 className="text-lg font-bold tracking-tight text-[rgb(var(--crm-text))] sm:text-xl">
              {activeMeta?.label}
            </h3>
            <p className="mt-1 text-xs text-[rgb(var(--crm-text-muted))]">
              {activeSection === 'general' && 'Workspace overview and quick preferences.'}
              {activeSection === 'appearance' && 'Choose how your CRM workspace looks.'}
              {activeSection === 'sp-api' && 'Manage Amazon SP-API credentials and renewal.'}
              {activeSection === 'assign-user' &&
                'Create staff logins and assign limited section access.'}
              {activeSection === 'account' && 'Your profile and account details.'}
            </p>
          </div>

          <div className="overflow-y-auto px-6 py-5">
            {activeSection === 'general' ? (
              <div className="crm-panel p-5">
                <h4 className="text-base font-semibold">Workspace</h4>
                <p className="crm-text-muted mt-1 text-sm">
                  Signed in as {user.name || user.email}
                  {user.role === 'workspace_user'
                    ? ' with limited section access. Use Appearance to change theme, or Account to review your profile and assigned sections.'
                    : '. Use the sections on the left to update appearance, SP-API credentials, assign users, or account details.'}
                </p>
              </div>
            ) : null}

            {activeSection === 'appearance' && token ? (
              <ThemeSettings embedded user={user} token={token} onUserUpdate={onUserUpdate} />
            ) : null}

            {activeSection === 'sp-api' && token ? (
              <SpApiSettings embedded user={user} token={token} onUserUpdate={onUserUpdate} />
            ) : null}

            {activeSection === 'assign-user' && token ? (
              <AssignUserSettings token={token} onClose={onClose} />
            ) : null}

            {activeSection === 'account' ? <AccountSettings user={user} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsModal;
